import React, { useEffect, useMemo, useImperativeHandle, forwardRef, useCallback, useState, useRef } from 'react';
import { Player, PlayerRef as RemotionPlayerRef } from '@remotion/player';
import { AbsoluteFill } from 'remotion';
import { Spin } from 'antd';
import { CompositorComposition } from './CompositorComposition';
import { CompositorRecordingData, CompositorManifest } from '../types/manifest';
import { useSourceResolver } from '../hooks/useSourceResolver';
import { useCompositorManifest } from '../hooks/useCompositorManifest';
import { DEFAULT_FPS } from '../constants';
import { framesToMs, msToFrames } from '../utils/frameCalculator';

async function preloadMedia(url: string, type: 'video' | 'image' | 'audio'): Promise<void> {
  return new Promise((resolve, reject) => {
    if (type === 'video' || type === 'audio') {
      const el = document.createElement(type === 'audio' ? 'audio' : 'video');
      el.preload = 'auto';
      if (type === 'video') (el as HTMLVideoElement).muted = true;
      
      let resolved = false;
      const cleanup = () => {
        if (resolved) return;
        resolved = true;
        el.removeEventListener('canplaythrough', handleCanPlay);
        el.removeEventListener('loadeddata', handleLoadedData);
        el.removeEventListener('error', handleError);
        el.src = '';
        el.load();
      };
      
      const handleCanPlay = () => {
        cleanup();
        resolve();
      };
      
      const handleLoadedData = () => {
        cleanup();
        resolve();
      };
      
      const handleError = () => {
        cleanup();
        reject(new Error(`Failed to preload ${type}: ${url}`));
      };
      
      el.addEventListener('canplaythrough', handleCanPlay);
      el.addEventListener('loadeddata', handleLoadedData);
      el.addEventListener('error', handleError);
      el.src = url;
      el.load();
      
      setTimeout(() => {
        cleanup();
        resolve();
      }, 10000);
    } else {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
      img.src = url;
      
      setTimeout(() => resolve(), 5000);
    }
  });
}

interface CompositorCompositionWrapperProps {
  manifest: CompositorManifest | null;
}

const CompositorCompositionWrapper: React.FC<CompositorCompositionWrapperProps> = ({ manifest }) => {
  if (!manifest) {
    return (
      <AbsoluteFill style={{ backgroundColor: '#1a1a1a' }} />
    );
  }
  return <CompositorComposition manifest={manifest} />;
};

export interface CompositorPlayerRef {
  play: () => void;
  pause: () => void;
  seekTo: (frame: number) => void;
  seekToMs: (ms: number) => void;
  seekToSeconds: (seconds: number) => void;
  getCurrentFrame: () => number;
  getCurrentTimeMs: () => number;
  isPlaying: () => boolean;
  toggle: () => void;
  getContainerNode: () => HTMLDivElement | null;
}

export interface CompositorPlayerProps {
  recordingData: CompositorRecordingData;
  pendingBlobs?: Map<string, Blob>;
  pendingBlobUrls?: Map<string, string>;
  segmentBlobs?: Map<string, Map<number, Blob>>;
  autoPlay?: boolean;
  loop?: boolean;
  initialFrame?: number;
  showControls?: boolean;
  fps?: number;
  style?: React.CSSProperties;
  className?: string;
  onTimeUpdate?: (timeMs: number, frame: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (error: Error) => void;
  onReady?: (manifest: CompositorManifest) => void;
}

export const CompositorPlayer = forwardRef<CompositorPlayerRef, CompositorPlayerProps>(
  (
    {
      recordingData,
      pendingBlobs = new Map(),
      pendingBlobUrls,
      segmentBlobs,
      autoPlay = false,
      loop = false,
      initialFrame = 0,
      showControls = true,
      fps = DEFAULT_FPS,
      style,
      className,
      onTimeUpdate,
      onPlay,
      onPause,
      onEnded,
      onError,
      onReady,
    },
    ref
  ) => {
    const playerRef = React.useRef<RemotionPlayerRef>(null);
    const { resolver, cleanup } = useSourceResolver();
    const [mediaPreloaded, setMediaPreloaded] = useState(false);
    const [preloadError, setPreloadError] = useState<string | null>(null);
    const preloadedManifestIdRef = useRef<string | null>(null);

    const manifest = useCompositorManifest({
      recordingData,
      pendingBlobs,
      pendingBlobUrls,
      segmentBlobs,
      sourceResolver: resolver,
      fps,
    });

    // Check if all required video and audio sources are ready (not in loading state)
    const allSourcesReady = useMemo(() => {
      if (!manifest) return false;
      
      // Check each layer's source status (both 'screen' and 'webcam' layers have video segments)
      for (const layer of manifest.layers) {
        if (layer.type === 'screen' || layer.type === 'webcam') {
          // Check all segments in this layer
          for (const segment of layer.segments) {
            if (segment.source.status === 'loading') {
              return false;
            }
          }
        }
      }
      
      // Check audio layer segments if present
      if (manifest.audioLayer?.segments) {
        for (const segment of manifest.audioLayer.segments) {
          if (segment.source.status === 'loading') {
            return false;
          }
        }
      }
      
      return true;
    }, [manifest]);

    // Preload all media files once manifest and sources are ready
    useEffect(() => {
      if (!manifest || !allSourcesReady) {
        return;
      }
      
      // Skip if already preloaded for this manifest
      if (preloadedManifestIdRef.current === manifest.id) {
        return;
      }
      
      const preloadAllMedia = async () => {
        setMediaPreloaded(false);
        setPreloadError(null);
        
        const mediaToPreload: { url: string; type: 'video' | 'image' | 'audio' }[] = [];
        
        // Collect video/avatar URLs from layers
        for (const layer of manifest.layers) {
          if (layer.type === 'screen' || layer.type === 'webcam') {
            for (const segment of layer.segments) {
              if (segment.isAvatar && segment.avatarUrl) {
                mediaToPreload.push({ url: segment.avatarUrl, type: 'image' });
              } else if (segment.source.status === 'ready' && segment.source.value) {
                mediaToPreload.push({ url: segment.source.value, type: 'video' });
              }
            }
          }
        }
        
        // Collect audio URLs — preload with <audio> element to validate playability
        if (manifest.audioLayer?.segments) {
          for (const segment of manifest.audioLayer.segments) {
            if (segment.source.status === 'ready' && segment.source.value) {
              mediaToPreload.push({ url: segment.source.value, type: 'audio' });
            }
          }
        }
        
        // Collect background image URL if present
        if (manifest.background.type === 'image' && manifest.background.imageUrl) {
          mediaToPreload.push({ url: manifest.background.imageUrl, type: 'image' });
        }
        
        try {
          // Preload all media in parallel with individual error handling
          await Promise.all(
            mediaToPreload.map(async ({ url, type }) => {
              try {
                await preloadMedia(url, type);
              } catch (err) {
                // If audio preload fails, mark its source as errored so AudioSegment won't render it
                if (type === 'audio' && manifest.audioLayer?.segments) {
                  for (const seg of manifest.audioLayer.segments) {
                    if (seg.source.value === url) {
                      seg.source.status = 'error';
                      seg.source.error = 'Audio source failed to load';
                    }
                  }
                }
              }
            })
          );
          
          preloadedManifestIdRef.current = manifest.id;
          setMediaPreloaded(true);
        } catch (err) {
          setPreloadError('Failed to preload some media');
          // Still mark as preloaded to allow playback
          setMediaPreloaded(true);
        }
      };
      
      preloadAllMedia();
    }, [manifest, allSourcesReady]);

    useEffect(() => {
      if (manifest && allSourcesReady && mediaPreloaded && onReady) {
        onReady(manifest);
      }
    }, [manifest, allSourcesReady, mediaPreloaded, onReady]);

    useEffect(() => {
      return () => {
        cleanup();
      };
    }, [cleanup]);

    const handleFrameUpdate = useCallback(
      (e: { detail: { frame: number } }) => {
        if (onTimeUpdate && manifest) {
          const frame = e.detail.frame;
          const timeMs = framesToMs(frame, manifest.fps);
          onTimeUpdate(timeMs, frame);
        }
      },
      [onTimeUpdate, manifest]
    );

    const handlePlay = useCallback(() => {
      onPlay?.();
    }, [onPlay]);

    const handlePause = useCallback(() => {
      onPause?.();
    }, [onPause]);

    const handleEnded = useCallback(() => {
      onEnded?.();
    }, [onEnded]);

    const handleError = useCallback(
      (e: Error) => {
        onError?.(e);
      },
      [onError]
    );

    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          playerRef.current?.play();
        },
        pause: () => {
          playerRef.current?.pause();
        },
        seekTo: (frame: number) => {
          playerRef.current?.seekTo(frame);
        },
        seekToMs: (ms: number) => {
          if (manifest) {
            const frame = msToFrames(ms, manifest.fps);
            playerRef.current?.seekTo(frame);
          }
        },
        seekToSeconds: (seconds: number) => {
          if (manifest) {
            const frame = msToFrames(seconds * 1000, manifest.fps);
            playerRef.current?.seekTo(frame);
          }
        },
        getCurrentFrame: () => {
          return playerRef.current?.getCurrentFrame() ?? 0;
        },
        getCurrentTimeMs: () => {
          if (manifest) {
            const frame = playerRef.current?.getCurrentFrame() ?? 0;
            return framesToMs(frame, manifest.fps);
          }
          return 0;
        },
        isPlaying: () => {
          return playerRef.current?.isPlaying() ?? false;
        },
        toggle: () => {
          playerRef.current?.toggle();
        },
        getContainerNode: () => {
          return playerRef.current?.getContainerNode() ?? null;
        },
      }),
      [manifest]
    );

    const inputProps = useMemo(() => {
      return { manifest };
    }, [manifest]);

    // Show loader if manifest is not ready, sources are loading, or media is preloading
    if (!manifest || !allSourcesReady || !mediaPreloaded) {
      return (
        <div
          style={{
            ...style,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#1a1a1a',
            color: '#888',
            minHeight: 200,
            aspectRatio: recordingData.layout?.canvas?.aspectRatio?.replace(':', '/') || '16/9',
            gap: 12,
          }}
          className={className}
        >
          <Spin size="large" />
          <span style={{ fontSize: 14 }}>
            {!manifest ? 'Preparing...' : !allSourcesReady ? 'Resolving sources...' : 'Loading video...'}
          </span>
        </div>
      );
    }

    return (
      <Player
        ref={playerRef}
        component={CompositorCompositionWrapper as React.ComponentType<unknown>}
        inputProps={inputProps}
        durationInFrames={manifest.durationFrames}
        compositionWidth={manifest.canvas.width}
        compositionHeight={manifest.canvas.height}
        fps={manifest.fps}
        style={{
          width: '100%',
          ...style,
        }}
        className={className}
        controls={showControls}
        autoPlay={autoPlay}
        loop={loop}
        initialFrame={initialFrame}
        clickToPlay={true}
        doubleClickToFullscreen={true}
        spaceKeyToPlayOrPause={true}
      />
    );
  }
);

CompositorPlayer.displayName = 'CompositorPlayer';
